import type { FieldDefinition, VectorFeatureCollection } from '../data/model';
import { attributeRuleField, attributeRuleFields } from '../rules/fields';

export function labelFields(fields: readonly FieldDefinition[]): readonly FieldDefinition[] {
  return attributeRuleFields(fields);
}

export function isLabelField(fields: readonly FieldDefinition[], fieldName: string): boolean {
  return attributeRuleField(fields, fieldName) !== null;
}

/** Initial suggestions use imported metadata and bounded value samples; manual scalar choices stay unrestricted. */
export function chooseInitialLabelField(
  fields: readonly FieldDefinition[],
  data?: VectorFeatureCollection,
): string | null {
  const preferredNames = [
    'name',
    'label',
    'title',
    'displayname',
    'place',
    'locality',
    'suburb',
    'city',
    'town',
    'roadname',
    'streetname',
    'routename',
    'road',
    'street',
    'route',
  ];
  const normalise = (name: string) => name.toLowerCase().replace(/[\s_-]/g, '');
  const candidates = labelFields(fields).filter((field) => {
    const name = normalise(field.name);
    return (
      field.type === 'string' &&
      field.presentCount > field.nullCount &&
      !/^(?:objectid|fid|oid|id|gid|uuid|guid|row|rowid|rownum|rownumber|geometryid|geomid|shapeid)$/.test(
        name,
      ) &&
      !/(?:[_\s-](?:id|uuid|guid)|Id|ID)$/.test(field.name)
    );
  });
  for (const name of preferredNames) {
    const candidate = candidates.find((field) => normalise(field.name) === name);
    if (candidate && hasUsefulSample(candidate.name, data)) return candidate.name;
  }
  return candidates.find((field) => hasUsefulSample(field.name, data))?.name ?? null;
}

/** Bounded inspection at import, never a second full-data scan or persistent index. */
function hasUsefulSample(field: string, data?: VectorFeatureCollection): boolean {
  if (!data) return true;
  const count = Math.min(64, data.features.length);
  for (let sample = 0; sample < count; sample++) {
    const index = count === 1 ? 0 : Math.floor((sample * (data.features.length - 1)) / (count - 1));
    const value = data.features[index]?.properties?.[field];
    if (typeof value !== 'string') continue;
    const text = value.trim();
    if (
      text.length > 0 &&
      text.length <= 160 &&
      !/[\r\n]/.test(text) &&
      /\p{L}/u.test(text) &&
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)
    )
      return true;
  }
  return false;
}
